import { Fragment, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { adminAPI } from 'services/admin';
import { ListItem } from '@mui/material';
import { Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import useInfiniteScroll from 'react-infinite-scroll-hook';

export const AsyncAutocomplete = ({
  field,
  label,
  control,
  optionsMap,
  requestUrl,
  responseKey,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const onLoadOptions = useMemo(
    () =>
      debounce(async (request, callback) => {
        try {
          setLoading(true);
          const newOffset = request?.offset || 0;
          const searchUrl = requestUrl
            .replace('#name', request.input)
            .replace('#offset', newOffset);

          const response = await adminAPI.get(searchUrl);
          const data = response[responseKey];
          const total = response.totalItemsCountFiltered;

          const optionsResult = optionsMap
            ? optionsMap(data)
            : data.map(data => ({
                label: data.name || data.key, // Posíveis labels
                value: data.id || data.userId, // Possíveis chaves para id
              }));

          const hasMoreOptions = newOffset + optionsResult.length < total;

          setHasNextPage(hasMoreOptions);
          callback(optionsResult);
        } catch (error) {
          return { options: [], hasMore: false };
        } finally {
          setLoading(false);
        }
      }, 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage: hasNextPage,

    onLoadMore: () => {
      if (!open) return;

      onLoadOptions({ input: inputValue, offset: options.length }, result => {
        const newArray = [...options, ...result];
        setOptions(newArray);
      });
    },
    disabled: false,
    rootMargin: '0px 0px 10px 0px',
  });

  useEffect(() => {
    let active = true;

    searchValue.length >= 3 &&
      onLoadOptions({ input: searchValue }, results => {
        if (active) {
          let newOptions = [];

          if (results) {
            newOptions = [...results];
          }

          setOptions(newOptions);
        }
      });

    return () => {
      active = false;
    };
  }, [searchValue, onLoadOptions]);

  useEffect(() => {
    // Search to prevent setValue on select option
    if (!options.some(option => option.label === inputValue)) {
      setSearchValue(inputValue);
    }
  }, [inputValue, options]);

  const shouldRenderSentry = () => {
    return hasNextPage || loading ? (
      <ListItem
        sx={{
          color: '#757575',
        }}
        ref={sentryRef}
        key="sentry">
        Carregando ...
      </ListItem>
    ) : null;
  };

  const renderOptions = (optionProps, option) => {
    const { id, label } = option;

    if (id === 'sentry') {
      return shouldRenderSentry();
    }

    return (
      <ListItem {...optionProps} key={id}>
        {label}
      </ListItem>
    );
  };

  return (
    <Controller
      name={field}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          open={open}
          size="small"
          name={field}
          ListboxProps={{ ref: rootRef }}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={option => option.label}
          options={options}
          renderOption={renderOptions}
          filterOptions={filterOptions => {
            // Add sentry to options
            const sentryOptionLabel = { id: 'sentry', label: '' };

            if (hasNextPage) {
              return [...filterOptions, sentryOptionLabel];
            }

            return filterOptions;
          }}
          loading={loading}
          value={value}
          onChange={(_event, newValue) => {
            if (loading) return;

            setOptions(options);
            onChange(newValue);
          }}
          noOptionsText="Nada encontrado"
          loadingText="Carregando ..."
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
